import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigationContext } from '../context';
import { WorkerSaveResearchFrequency } from '@restworld/utility-types';
import { IUBENDA_PURPOSE, onIubendaConsent } from '../utils/iubenda-consent';

export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;
const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

export function useFacebookPixel() {
  const [isActive, setIsActive] = useState<boolean>(false);
  const { router } = useNavigationContext();
  // Guards against double init when Iubenda re-emits consent on subsequent events.
  const initializedRef = useRef(false);

  const init = useCallback(async () => {
    if (initializedRef.current) return;
    if (!FB_PIXEL_ID || typeof window === 'undefined') return;

    initializedRef.current = true;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    setIsActive(true);
    ReactPixel.init(FB_PIXEL_ID, undefined, { autoConfig: true, debug: !isProd });
    ReactPixel.pageView();
  }, []);

  useEffect(() => {
    // The Meta Pixel is NOT covered by Google Consent Mode, so we gate its
    // initialisation on the user's Iubenda consent for Targeting & Advertising
    // (purpose 5). Until then no Pixel script loads and no PageView fires.
    // Mirrors how the marketing website gates PostHog. Iubenda's
    // `reloadOnConsent: true` reloads the page after the user accepts, and the
    // bridge re-emits the stored preference on reload, triggering init here.
    let routeHandler: (() => Promise<void>) | undefined;

    const unsubscribe = onIubendaConsent(IUBENDA_PURPOSE.ADVERTISING, (granted) => {
      if (!granted) return;
      init();
      if (!routeHandler) {
        routeHandler = async () => {
          const ReactPixel = (await import('react-facebook-pixel')).default;
          ReactPixel.pageView();
        };
        router?.events.on('routeChangeComplete', routeHandler);
      }
    });

    return () => {
      unsubscribe();
      if (routeHandler) router?.events.off('routeChangeComplete', routeHandler);
    };
  }, [router?.events, init]);

  const trackWorkerApplied = useCallback(async () => {
    if (!isActive) return;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    ReactPixel.trackCustom('WorkerApplied', {
      content_category: 'B2C',
    });
  }, [isActive]);

  const trackWorkerStartsApplication = useCallback(async () => {
    if (!isActive) return;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    ReactPixel.trackCustom('WorkerStartApplication', {
      content_category: 'B2C',
    });
  }, [isActive]);

  const trackWorkerSavedResearch = useCallback(
    async ({ user_id, frequency }: { user_id?: string; frequency?: WorkerSaveResearchFrequency }) => {
      if (!isActive) return;
      const ReactPixel = (await import('react-facebook-pixel')).default;
      ReactPixel.trackCustom('WorkerSaveResearch', { user_id, frequency, content_category: 'B2C' });
    },
    [isActive]
  );

  const trackWorkerAppliesSpontaneously = useCallback(async () => {
    if (!isActive) return;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    ReactPixel.trackCustom('WorkerAppliesSpontaneously', {
      content_category: 'B2C',
    });
  }, [isActive]);

  const trackEmployerSignup = useCallback(async () => {
    if (!isActive) return;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    ReactPixel.track('CompleteRegistration', {
      content_name: 'Employer Signup',
      content_category: 'B2B',
    });
  }, [isActive]);

  const trackPurchase = useCallback(async () => {
    if (!isActive) return;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    ReactPixel.track('Purchase', {
      content_category: 'B2B',
      currency: 'EUR',
      value: 19.9,
    });
  }, [isActive]);

  const trackPublishJP = useCallback(async () => {
    if (!isActive) return;
    const ReactPixel = (await import('react-facebook-pixel')).default;
    ReactPixel.trackCustom('PublishJP', {
      content_category: 'B2B',
    });
  }, [isActive]);

  return {
    isActive,
    trackWorkerApplied,
    trackWorkerStartsApplication,
    trackWorkerSavedResearch,
    trackEmployerSignup,
    trackWorkerAppliesSpontaneously,
    trackPurchase,
    trackPublishJP,
  };
}
